import { useEffect, useRef, useState } from 'react';

export function useDragAndDrop<T extends HTMLElement>({
    dragAxis = 'xy'
}: {
    dragAxis: 'xy' | 'x' | 'y';
}) {
    const refDraggableElement = useRef<T | null>(null);
    const [isDragging, setDragging] = useState(false);
    let startPosition = { scrollTop: 0, scrollLeft: 0, clientX: 0, clientY: 0 };

    const DRAG_SENSITIVITY_MULTIPLIER = 1.5;

    const hasMoved = (
        { clientX: startX, clientY: startY }: typeof startPosition,
        { clientX: endX, clientY: endY }: MouseEvent
    ) => {
        if (dragAxis.includes('x') && startX !== endX) {
            return true;
        }

        if (dragAxis.includes('y') && startY !== endY) {
            return true;
        }

        return false;
    };

    const handleDragStart = (event: MouseEvent) => {
        if (refDraggableElement.current === null) {
            return;
        }

        refDraggableElement.current?.addEventListener(
            'mousemove',
            handleDragMove
        );
        window?.addEventListener('mouseup', handleDragStop, true);

        setDragging(true);

        startPosition = {
            scrollLeft: refDraggableElement.current.scrollLeft,
            scrollTop: refDraggableElement.current.scrollTop,
            clientX: event.clientX,
            clientY: event.clientY
        };
    };

    const handleDragMove = (event: MouseEvent) => {
        if (refDraggableElement.current === null) {
            return;
        }

        if (dragAxis.includes('x')) {
            const differenceX =
                (event.clientX - startPosition.clientX) *
                DRAG_SENSITIVITY_MULTIPLIER;

            refDraggableElement.current.scrollLeft =
                startPosition.scrollLeft - differenceX;
        }

        if (dragAxis.includes('y')) {
            const differenceY =
                (event.clientY - startPosition.clientY) *
                DRAG_SENSITIVITY_MULTIPLIER;

            refDraggableElement.current.scrollTop =
                startPosition.scrollTop - differenceY;
        }

        if (hasMoved(startPosition, event)) {
            disableLinks();
        }
    };

    const handleDragStop = (event: MouseEvent) => {
        refDraggableElement.current?.removeEventListener(
            'mousemove',
            handleDragMove
        );
        window?.removeEventListener('mouseup', handleDragStop, true);
        refDraggableElement.current?.removeEventListener(
            'mouseleave',
            handleDragStop
        );

        setDragging(false);

        if (hasMoved(startPosition, event)) {
            enableLinks();
        }
    };

    const disableLinks = () => {
        refDraggableElement.current
            ?.querySelectorAll('a')
            .forEach((element) => {
                element.style.pointerEvents = 'none';
            });
    };

    const enableLinks = () => {
        refDraggableElement.current
            ?.querySelectorAll('a')
            .forEach((element) => {
                setTimeout(() => {
                    element.style.pointerEvents = 'initial';
                }, 700);
            });
    };

    useEffect(() => {
        if (refDraggableElement.current) {
            refDraggableElement.current.style.cursor = isDragging
                ? 'grabbing'
                : 'grab';
        }
    }, [isDragging]);

    useEffect(() => {
        const { current } = refDraggableElement;

        current?.addEventListener('mousedown', handleDragStart);
        current?.addEventListener('mouseleave', handleDragStop);

        return () => {
            current?.removeEventListener('mousedown', handleDragStart);
            current?.removeEventListener('mousemove', handleDragMove);
            current?.removeEventListener('mouseleave', handleDragStop);
            window?.removeEventListener('mouseup', handleDragStop, true);
            enableLinks();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refDraggableElement.current]);

    return { refDraggableElement, isDragging };
}
